
interface Topic {
  id: string;
  title: string;
  content: string;
  author: {
    id: string;
    name: string;
    avatar: string;
    isOnline: boolean;
    reputation: number;
  };
  category: string;
  sessionId?: string;
  createdAt: string;
  replies: number;
  views: number;
  likes: number;
  isPinned: boolean;
  tags: string[];
}

interface TopicCardProps {
  topic: Topic;
}

export default function TopicCard({ topic }: TopicCardProps) {
  const timeAgo = (date: string) => {
    const now = new Date();
    const past = new Date(date);
    const diffInHours = Math.floor((now.getTime() - past.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    return `${Math.floor(diffInHours / 24)}d ago`;
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'sessions': return 'bg-green-100 text-green-700';
      case 'music-discovery': return 'bg-emerald-100 text-emerald-700';
      case 'equipment': return 'bg-teal-100 text-teal-700';
      case 'community': return 'bg-cyan-100 text-cyan-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'sessions': return 'ri-headphone-line';
      case 'music-discovery': return 'ri-music-2-line';
      case 'equipment': return 'ri-equalizer-line';
      case 'community': return 'ri-group-line';
      default: return 'ri-discuss-line';
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-green-100 p-6 hover:shadow-md transition-shadow duration-200">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="relative">
            <img
              src={topic.author.avatar}
              alt={topic.author.name}
              className="w-10 h-10 rounded-full object-cover object-top"
            />
            {topic.author.isOnline && (
              <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-500 border-2 border-white rounded-full"></div>
            )}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-900 text-sm">{topic.author.name}</span>
              <span className="text-xs text-gray-500">•</span>
              <span className="text-xs text-gray-500">{timeAgo(topic.createdAt)}</span>
              {topic.isPinned && (
                <i className="ri-pushpin-line text-green-600 text-sm"></i>
              )}
            </div>
            <div className="flex items-center gap-2 mt-1">
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(topic.category)} flex items-center gap-1`}>
                <i className={`${getCategoryIcon(topic.category)} text-xs`}></i>
                {topic.category.replace('-', ' ')}
              </span>
              {topic.sessionId && (
                <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium flex items-center gap-1">
                  <i className="ri-link text-xs"></i>
                  Linked Session
                </span>
              )}
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">{topic.views} views</span>
          <button className="text-gray-400 hover:text-gray-600 cursor-pointer">
            <i className="ri-more-line"></i>
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-2 hover:text-green-600 cursor-pointer transition-colors">
          {topic.title}
        </h3>
        <p className="text-gray-600 text-sm line-clamp-2">
          {topic.content}
        </p>
      </div>

      {/* Tags */}
      {topic.tags.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {topic.tags.slice(0, 4).map((tag) => (
            <span
              key={tag}
              className="px-2 py-1 bg-gray-100 text-gray-600 rounded-md text-xs hover:bg-gray-200 cursor-pointer transition-colors"
            >
              #{tag}
            </span>
          ))}
          {topic.tags.length > 4 && (
            <span className="text-xs text-gray-500">+{topic.tags.length - 4} more</span>
          )}
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-100">
        <div className="flex items-center gap-4">
          <button className="flex items-center gap-2 text-gray-600 hover:text-green-600 transition-colors cursor-pointer">
            <i className="ri-heart-line text-sm"></i>
            <span className="text-sm">{topic.likes}</span>
          </button>
          <button className="flex items-center gap-2 text-gray-600 hover:text-green-600 transition-colors cursor-pointer">
            <i className="ri-chat-3-line text-sm"></i>
            <span className="text-sm">{topic.replies} replies</span>
          </button>
          <button className="flex items-center gap-2 text-gray-600 hover:text-green-600 transition-colors cursor-pointer">
            <i className="ri-share-line text-sm"></i>
            <span className="text-sm">Share</span>
          </button>
        </div>
        
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">Rep: {topic.author.reputation}</span>
          {topic.sessionId && (
            <button className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-3 py-1 rounded-md text-xs hover:from-green-600 hover:to-emerald-700 transition-all duration-200 cursor-pointer whitespace-nowrap">
              Join Session
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
